import {
    defaultLayoutForScreen,
    HomeLayoutResponse,
    HomeScreenId,
    HomeSection,
    homeSectionDefaultVisibleWhenForScreen,
    homeSectionTypesForScreen,
    isGateableSectionType,
} from '@numeo/types'

export interface EditorRow {
    /** The catalog type (or the opaque unknown type). */
    type: string
    /** UI toggle. Disabled rows are persisted as `enabled: false`, not omitted. */
    enabled: boolean
    /** The actual section descriptor that will be round-tripped/saved. */
    section: HomeSection
    /** Type this Gluon build has no editor for — shown read-only and round-tripped verbatim. */
    unknown?: boolean
}

/** Section types the app no longer has; dropped on load so the next save removes them. */
const RETIRED_SECTION_TYPES = new Set(['highlights'])

/** Build the editor row list from a layout: stored sections in order, then any default singletons the layout predates. */
export function rowsFromLayout(layout: HomeLayoutResponse, screen: HomeScreenId): EditorRow[] {
    const sectionTypes = homeSectionTypesForScreen(screen)
    const defaultVisibleWhen = homeSectionDefaultVisibleWhenForScreen(screen)
    const presentSingletons = new Set<string>()
    const rows: EditorRow[] = []
    for (const s of layout.sections) {
        const type = s.type
        if (RETIRED_SECTION_TYPES.has(type)) continue
        const enabled = s.enabled !== false
        if (isGateableSectionType(type)) {
            rows.push({ type, enabled, section: s })
        } else if (sectionTypes.includes(type)) {
            rows.push({ type, enabled, section: s })
            presentSingletons.add(type)
        } else {
            // Forward-compat: preserve section types this Gluon build cannot edit.
            rows.push({ type, enabled, section: s, unknown: true })
        }
    }
    // A missing default section means the stored layout predates the current
    // default. Intentional hides are explicit tombstones: `enabled: false`.
    const shippedByDefault = new Set(defaultLayoutForScreen(screen).sections.map((s) => s.type))
    for (const type of sectionTypes) {
        if (!presentSingletons.has(type)) {
            rows.push({
                type,
                enabled: shippedByDefault.has(type),
                section: { type, id: type, ...(defaultVisibleWhen[type] ? { visibleWhen: defaultVisibleWhen[type] } : {}) },
            })
        }
    }
    return rows
}

/** Reduce editor rows back to a layout payload, preserving explicit hidden rows. */
export function layoutFromRows(rows: EditorRow[], screen: HomeScreenId): HomeLayoutResponse {
    return {
        screen,
        version: defaultLayoutForScreen(screen).version,
        sections: rows.map((r) => {
            if (!r.enabled) return { ...r.section, enabled: false }
            const section = { ...r.section }
            delete section.enabled
            return section
        }),
    }
}
